import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef } from 'react';
import { useController } from 'react-hook-form';
import {
  DropdownArrowContainer,
  InputContainer,
  SearchInput,
  OptionsList,
  OptionItem,
  OptionItemContent,
  OptionName,
  OptionDetails,
  PriceTags,
  PriceTag,
  NoOptionsMessage,
  LoadMoreButton,
  ClearButton
} from './InfiniteSelect.style';

interface Option {
  id: any;
  name: string;
  code?: string;
  disabled?: boolean;
  category?: string;
  type?: string;
  cnpj?: string;
  contato?: string;
  telefone?: string;
  contactName?: string;
  phone?: string;
  description?: string;
  prices?: Array<{
    id?: number | string;
    price: string;
    unitOfMeasurement?: {
      id: number;
      name: string;
    };
  }>;
  stage?: {
    name: string;
    category: string;
    type: string;
  };
}

  import ReactDOM from 'react-dom';
interface InfiniteSelectProps {
  name?: string;
  control?: any;
  
  value?: Option | null;
  onSelect?: (option: Option | null) => void;
  options?: Option[];
  
  placeholder?: string;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
  emptyOptionsMessage?: string;
  
  fetchFunction?: (limit: number, offset: number, search?: string) => Promise<Option[]>;
  initialOptions?: Option[];
  
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  
  disabled?: boolean;
  hasError?: boolean;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  getOptionLabel?: (option: Option) => string;
  
  validateSelection?: (selectedOption: Option | null) => boolean;
}

interface UseInfiniteDataOptions<T> {
  fetchFunction: (limit: number, offset: number, search?: string) => Promise<T[]>;
  pageSize: number;
  initialData?: T[];
}

interface UseInfiniteDataReturn<T> {
  data: T[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  resetAndSearch: (search: string) => void;
  searchValue: string;
  initialize: () => void;
}

function useInfiniteData<T extends { id: any }>({
  fetchFunction,
  pageSize,
  initialData = []
}: UseInfiniteDataOptions<T>): UseInfiniteDataReturn<T> {
  const [data, setData] = useState<T[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const fetchFunctionRef = useRef(fetchFunction);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
    setData([]);
    setOffset(0);
    setHasMore(true);
    isInitializedRef.current = false;
  }, [fetchFunction]);

  const performFetch = useCallback(async (search: string, currentOffset: number) => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const newData = await fetchFunctionRef.current(pageSize, currentOffset, search);

      setData(prevData => {
        const combinedData = currentOffset === 0 ? newData : [...prevData, ...newData];
        return Array.from(new Map(combinedData.map(item => [item.id, item])).values());
      });
      setOffset(currentOffset + newData.length);
      setHasMore(newData.length === pageSize);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [pageSize]);

  const resetAndSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchValue(search);

    searchTimeoutRef.current = setTimeout(() => {
      setData([]);
      setOffset(0);
      setHasMore(true);
      performFetch(search, 0);
    }, 300);
  }, [performFetch]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    performFetch(searchValue, offset);
  }, [hasMore, isLoading, performFetch, searchValue, offset]);

  const initialize = useCallback(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      performFetch('', 0);
    }
  }, [performFetch]);

  return {
    data,
    isLoading,
    hasMore,
    loadMore,
    resetAndSearch,
    searchValue,
    initialize
  };
}

const InfiniteSelect = forwardRef<HTMLInputElement, InfiniteSelectProps>(({
  name,
  control,
  value: controlledValue,
  onSelect: onSelectProp,
  options: controlledOptions,
  placeholder,
  searchPlaceholder = 'Buscar...',
  noOptionsMessage,
  emptyOptionsMessage,
  fetchFunction,
  initialOptions = [],
  isLoading: controlledIsLoading,
  onLoadMore: controlledOnLoadMore,
  hasMore: controlledHasMore,
  searchValue: controlledSearchValue,
  onSearchChange: controlledOnSearchChange,
  disabled = false,
  hasError,
  onFocus: onFocusProp,
  onKeyDown,
  getOptionLabel,
  validateSelection
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [shouldOpenOnFocus, setShouldOpenOnFocus] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const controllerProps = control && name ? useController({
    name,
    control,
  }) : null;

  const fieldValue = controllerProps?.field.value || controlledValue;
  const fieldOnChange = controllerProps?.field.onChange || onSelectProp;
  const fieldRef = controllerProps?.field.ref || ref;
  
  const setRefs = useCallback((element: HTMLInputElement | null) => {
    if (fieldRef && typeof fieldRef === 'function') {
      fieldRef(element);
    } else if (fieldRef && 'current' in fieldRef) {
      (fieldRef as any).current = element;
    }
  }, [fieldRef]);

  const {
    data: infiniteData,
    isLoading: infiniteIsLoading,
    hasMore: infiniteHasMore,
    loadMore: infiniteLoadMore,
    resetAndSearch: infiniteResetAndSearch,
    searchValue: infiniteSearchValue,
    initialize: infiniteInitialize
  } = useInfiniteData({
    fetchFunction: fetchFunction || (() => Promise.resolve([])),
    pageSize: 10,
    initialData: initialOptions,
  });

  const options = controlledOptions || infiniteData;
  const isLoading = controlledIsLoading !== undefined ? controlledIsLoading : infiniteIsLoading;
  const hasMore = controlledHasMore !== undefined ? controlledHasMore : infiniteHasMore;
  const loadMore = controlledOnLoadMore || infiniteLoadMore;
  const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : infiniteSearchValue;
  const onSearchChange = controlledOnSearchChange || infiniteResetAndSearch;

  const getDisplayValue = useCallback((val: Option | null) => {
    if (!val) return '';
    if (typeof getOptionLabel === 'function') return getOptionLabel(val);
    
    if (typeof val !== 'object' || !val.name) return '';
    
    return val.code ? `${val.code} - ${val.name}` : val.name;
  }, [getOptionLabel]);

  useEffect(() => {
    if (!isOpen) {
      setLocalSearchValue(getDisplayValue(fieldValue));
    }
  }, [fieldValue, getDisplayValue, isOpen]);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const isInsideContainer = containerRef.current && containerRef.current.contains(target);
    const isInsidePortal = portalRef.current && portalRef.current.contains(target);
    if (!isInsideContainer && !isInsidePortal) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    if (shouldOpenOnFocus) {
      setIsOpen(true);
    }
    
    if (onFocusProp) {
      onFocusProp();
    }
    if (infiniteInitialize) {
      infiniteInitialize();
    }
    
    if (fieldValue && shouldOpenOnFocus) {
      setLocalSearchValue(fieldValue.name);
      setTimeout(() => {
        const length = e.target.value.length;
        e.target.setSelectionRange(length, length);
      }, 50);
    } else if (!fieldValue) {
      setLocalSearchValue('');
    }
  };

  const handleSelect = useCallback((option: Option) => {
    if (option.disabled) return;
    
    if (validateSelection) {
      if (validateSelection(option)) {
        if (fieldOnChange) {
          fieldOnChange(option);
        }
      }
    } else {
      if (fieldOnChange) {
        fieldOnChange(option);
      }
    }
    
    setLocalSearchValue(getDisplayValue(option));
    setIsOpen(false);
    setShouldOpenOnFocus(false);
    
    requestAnimationFrame(() => {
      const inputElement = document.querySelector(`input[name="${name}"]`) as HTMLInputElement;
      
      if (inputElement) {
        inputElement.focus();
        const length = inputElement.value.length;
        inputElement.setSelectionRange(length, length);
      }
      
      setTimeout(() => {
        setShouldOpenOnFocus(true);
      }, 100);
    });
  }, [fieldOnChange, validateSelection, getDisplayValue, fieldRef]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fieldOnChange) {
      fieldOnChange(null);
    }
    setLocalSearchValue('');
  };

  const handleSearchChange = (val: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setLocalSearchValue(val);
    if (onSearchChange) {
      onSearchChange(val);
    }
  };

  const currentSearchValue = localSearchValue;

  const getDisplayLabel = (option: Option) => {
    if (typeof getOptionLabel === 'function') {
      return getOptionLabel(option);
    }
    return option.name;
  };

  const optionItems = useMemo(() => options.map((option) => (
    <OptionItem
      key={option.id}
      onClick={() => handleSelect(option)}
      disabled={option.disabled}
      isHighlighted={!!(fieldValue && fieldValue.id === option.id)}
    >
      <OptionItemContent>
        <OptionName>{getDisplayLabel(option)}</OptionName>
        {(option.category || option.type || option.cnpj || option.telefone || option.phone || option.description || option.id) && (
          <OptionDetails>
            {option.description ? (
              <span>Descrição: {option.description}</span>
            ) : option.id && (
              <span>ID: {typeof option.id === 'object' ? (option.id as any)?.id || (option.id as any)?.name || 'N/A' : option.id}</span>
            )}
            {option.category && <span>Categoria: {typeof option.category === 'object' ? (option.category as any)?.name || 'N/A' : option.category}</span>}
            {option.type && <span>Tipo: {typeof option.type === 'object' ? (option.type as any)?.name || 'N/A' : option.type}</span>}
            {option.cnpj && <span>CNPJ: {typeof option.cnpj === 'object' ? (option.cnpj as any)?.name || 'N/A' : option.cnpj}</span>}
            {(option.telefone || option.phone) && (
              <span>Tel: {
                (() => {
                  const phone = option.telefone || option.phone;
                  return typeof phone === 'object' ? (phone as any)?.name || 'N/A' : phone;
                })()
              }</span>
            )}
          </OptionDetails>
        )}
        {option.prices && option.prices.length > 0 && (
          <PriceTags>
            {option.prices.map((price, index) => (
              <PriceTag key={index}>
                {/* unidade chega em 3 formatos: unit (lookup steel-orders), unitOfMeasurement (camelCase), UnitOfMeasurement (Prisma cru) */}
                R$ {parseFloat(price.price).toFixed(2)}/{(price as any).unit || price.unitOfMeasurement?.name || (price as any).UnitOfMeasurement?.name || 'unidade'}
              </PriceTag>
            ))}
          </PriceTags>
        )}
      </OptionItemContent>
    </OptionItem>
  )), [options, fieldValue, handleSelect]);

  return (
    <>
      <InputContainer ref={containerRef}>
        <SearchInput
          ref={setRefs}
          type="text"
          value={currentSearchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          hasError={!!hasError}
          isOpen={isOpen}
          name={name}
          autoComplete="off"
          data-lpignore="true"
        />
        {fieldValue && (
          <ClearButton onClick={handleClear} title="Limpar">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M1 11L11 1" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </ClearButton>
        )}
        <DropdownArrow isOpen={isOpen} />
      </InputContainer>
      {isOpen && dropdownPosition && ReactDOM.createPortal(
        <div
          ref={portalRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 9100,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '0 0 4px 4px',
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          {isLoading && options.length === 0 && <OptionItem disabled>Carregando...</OptionItem>}
          {!isLoading && options.length === 0 && (
            <NoOptionsMessage>
              {currentSearchValue
                ? 'Nenhum resultado encontrado'
                : (noOptionsMessage || emptyOptionsMessage || searchPlaceholder || 'Nenhum item')}
            </NoOptionsMessage>
          )}
          {optionItems}
          {hasMore && (
            <LoadMoreButton
              onClick={loadMore}
              disabled={isLoading}
            >
              {isLoading ? 'Carregando...' : 'Carregar mais'}
            </LoadMoreButton>
          )}
        </div>,
        document.body
      )}
    </>
  );
});

const DropdownArrow: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  return (
    <DropdownArrowContainer isOpen={isOpen}>
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#696969" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </DropdownArrowContainer>
  );
};

InfiniteSelect.displayName = 'InfiniteSelect';

export default InfiniteSelect; 